import { useNavigate } from 'react-router-dom';
import IconSvg from 'src/assets/icons/IconSvg';
import CancelRaffleBtn from '../components/CancelRaffleBtn';
import CreatedRaffleCard from '../components/CreatedRaffleCard';
import { useViewport } from 'src/context/ViewportContext';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import type { Raffle } from 'src/types/raffle';

const satsToBtc = (sats: number) => (sats / 1e8).toFixed(4);

const statusConfig: Record<string, { label: string; color: string; icon?: string }> = {
  DRAFT: { label: 'Pending', color: 'text-system-error', icon: 'clockS' },
  LIVE: { label: 'Live', color: 'text-system-success', icon: 'liveS' },
  ENDED: { label: 'Ended', color: 'text-system-error' },
  COMPLETED: { label: 'Completed', color: 'text-system-success' },
  CANCELLED: { label: 'Cancelled', color: 'text-grayscale-400' },
};

interface Props {
  activeView: 'list' | 'grid';
  raffles: Raffle[];
  loading: boolean;
  userId: string;
  onCancelled: () => void;
}

const CreatedRaffleTab = ({ activeView, raffles, loading, userId, onCancelled }: Props) => {
  if (loading) {
    return <div className="py-10 text-center text-grayscale-400">Loading raffles...</div>;
  }

  if (raffles.length === 0) {
    return <div className="py-10 text-center text-grayscale-400">No raffles found</div>;
  }

  if (activeView === 'list') {
    return <CreatedRaffleTable raffles={raffles} userId={userId} onCancelled={onCancelled} />;
  }

  return (
    <div className="grid gap-[18px] grid-cols-[repeat(auto-fill,minmax(320px,1fr))]">
      {raffles.map((raffle) => (
        <CreatedRaffleCard key={raffle.id} raffle={raffle} userId={userId} onCancelled={onCancelled} />
      ))}
    </div>
  );
};

export default CreatedRaffleTab;

interface TableProps {
  raffles: Raffle[];
  userId: string;
  onCancelled: () => void;
}

const CreatedRaffleTable = ({ raffles, userId, onCancelled }: TableProps) => {
  const { isMobile } = useViewport();
  const navigate = useNavigate();

  return (
    <>
      {!isMobile ? (
        <div className="py-3 rounded-lg bg-grayscale-900">
          <table className="w-full">
            <thead className="text-sm text-left text-grayscale-300">
              <tr>
                <th className="w-[348px] py-[13px] pl-[58px]">Raffle Name</th>

                <th className="w-[188px] py-[13px]">
                  <div className="flex gap-1 items-center">
                    <span>Sold / Total Tickets</span>
                    <IconSvg icon="trianglesS" />
                  </div>
                </th>

                <th className="w-[188px] py-[13px]">Total Tickets Value</th>

                <th className="w-[188px] py-[13px]">
                  <div className="flex gap-1 items-center">
                    <span>Ticket Price</span>
                    <IconSvg icon="trianglesS" />
                  </div>
                </th>

                <th className="w-[188px] py-[13px]">
                  <div className="flex gap-1 items-center">
                    <span>End Time</span>
                    <IconSvg icon="trianglesS" />
                  </div>
                </th>

                <th className="py-5 w-[150px]"></th>
              </tr>
            </thead>

            <tbody>
              <tr style={{ height: '10px' }}></tr>

              {raffles.map((raffle) => {
                const status = statusConfig[raffle.status] ?? statusConfig.DRAFT;
                const endDate = new Date(raffle.endDate);
                const formattedEndDate = `${endDate.getDate().toString().padStart(2, '0')}/${(endDate.getMonth() + 1).toString().padStart(2, '0')}/${endDate.getFullYear()}`;

                return (
                  <tr
                    className="text-lg rounded-lg transition-all duration-200 cursor-pointer text-grayscale-300 hover:bg-grayscale-700"
                    key={raffle.id}
                    onClick={() => navigate(`/raffle/${raffle.id}`)}
                  >
                    <td className="py-3.5 pl-5 rounded-l-lg">
                      <div className="flex gap-3 items-center">
                        {(raffle.ordinalImageUrl || raffle.runesImageUrl) ? (
                          <img src={(raffle.ordinalImageUrl || raffle.runesImageUrl)!} alt="" className="flex-shrink-0 w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <div className="flex-shrink-0 w-12 h-12 bg-grayscale-600 rounded-full flex items-center justify-center text-xs text-grayscale-300">
                            {raffle.prizeType === 'RUNES' ? 'R' : '?'}
                          </div>
                        )}

                        <div className="space-y-1">
                          <div className="flex gap-1 items-center">
                            <div className="text-lg font-semibold text-brand-magnolia">
                              {raffle.name ?? 'Untitled'}
                            </div>
                            <IconSvg icon="verifiedS" className="text-brand-tangerine" />
                          </div>

                          <div className={`flex gap-1 items-center ${status.color}`}>
                            {status.icon && <IconSvg icon={status.icon as any} />}
                            <span className="text-xss">{status.label}</span>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td>
                      {raffle.ticketsSold} / {raffle.supply.toLocaleString()}
                    </td>

                    <td>
                      <div>
                        <div className="flex gap-1 items-center text-grayscale-100">
                          <IconSvg icon="bitcoinS" />
                          <div className="text-lg font-semibold text-grayscale-100">
                            {satsToBtc(raffle.totalTicketValue)}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div>
                        <div className="flex gap-1 items-center text-grayscale-100">
                          <IconSvg icon="bitcoinS" />
                          <div className="text-lg font-semibold text-grayscale-100">
                            {satsToBtc(raffle.ticketPriceSats)}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td>{formattedEndDate}</td>

                    <td className="pr-5 rounded-r-lg">
                      {(raffle.status === 'DRAFT' || raffle.status === 'LIVE') && (
                        <div className="flex justify-end items-center" onClick={e => e.stopPropagation()}>
                          <CancelRaffleBtn
                            raffleId={raffle.id}
                            ticketsSold={raffle.ticketsSold}
                            status={raffle.status}
                            userId={userId}
                            onCancelled={onCancelled}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid gap-2.5">
          {raffles.map((raffle) => {
            const status = statusConfig[raffle.status] ?? statusConfig.DRAFT;
            const endDate = new Date(raffle.endDate);
            const formattedEndDate = `${endDate.getDate().toString().padStart(2, '0')}/${(endDate.getMonth() + 1).toString().padStart(2, '0')}/${endDate.getFullYear()}`;

            return (
              <Disclosure
                as="div"
                className="overflow-hidden relative p-4 rounded-lg bg-grayscale-800 cursor-pointer"
                key={raffle.id}
                onClick={() => navigate(`/raffle/${raffle.id}`)}
              >
                <DisclosureButton className="flex relative justify-between items-center w-full rounded-lg group" onClick={e => e.stopPropagation()}>
                  <div className="flex gap-3 items-center">
                    {(raffle.ordinalImageUrl || raffle.runesImageUrl) ? (
                      <img src={(raffle.ordinalImageUrl || raffle.runesImageUrl)!} alt="" className="flex-shrink-0 w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="flex-shrink-0 w-12 h-12 bg-grayscale-600 rounded-full flex items-center justify-center text-xs text-grayscale-300">
                        {raffle.prizeType === 'RUNES' ? 'R' : '?'}
                      </div>
                    )}

                    <div className="space-y-1">
                      <div className="flex gap-1 items-center">
                        <div className="text-lg font-semibold text-brand-magnolia">
                          {raffle.name ?? 'Untitled'}
                        </div>
                        <IconSvg icon="verifiedS" className="text-brand-tangerine" />
                      </div>

                      <div className={`flex gap-1 items-center ${status.color}`}>
                        {status.icon && <IconSvg icon={status.icon as any} />}
                        <span className="text-xss">{status.label}</span>
                      </div>
                    </div>
                  </div>

                  <IconSvg
                    icon="arrowDownM"
                    className="text-grayscale-300 group-data-[open]:rotate-180 transition-all duration-200"
                  />
                </DisclosureButton>

                <DisclosurePanel className="!mt-3 transition-all duration-200">
                  <div className="flex justify-between items-start py-3 border-t border-grayscale-700">
                    <div>
                      <div className="text-xs text-grayscale-300">Ticket Price</div>
                      <div className="flex gap-1 items-center text-grayscale-100">
                        <IconSvg icon="bitcoinS" />
                        <div className="font-semibold text-grayscale-100">{satsToBtc(raffle.ticketPriceSats)}</div>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-xs text-grayscale-300">Sold / Total Tickets</div>
                      <div className="font-semibold text-grayscale-100">
                        {raffle.ticketsSold} / {raffle.supply.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-start py-3 border-t border-grayscale-700">
                    <div>
                      <div className="text-xs text-grayscale-300">Total Tickets Value</div>
                      <div className="flex gap-1 items-center text-grayscale-100">
                        <IconSvg icon="bitcoinS" />
                        <div className="font-semibold text-grayscale-100">{satsToBtc(raffle.totalTicketValue)}</div>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-xs text-grayscale-300">End Time:</div>
                      <div className="font-semibold text-grayscale-100">{formattedEndDate}</div>
                    </div>
                  </div>

                  {(raffle.status === 'DRAFT' || raffle.status === 'LIVE') && (
                    <CancelRaffleBtn
                      raffleId={raffle.id}
                      ticketsSold={raffle.ticketsSold}
                      status={raffle.status}
                      userId={userId}
                      onCancelled={onCancelled}
                    />
                  )}
                </DisclosurePanel>
              </Disclosure>
            );
          })}
        </div>
      )}
    </>
  );
};
